"use client"
import { useRouter, useParams } from 'next/navigation';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    TransformWrapper,
    TransformComponent,
} from "react-zoom-pan-pinch";
import '../../common_css/layout-management.css';
import '../../common_css/plot-modal-overlay.css';
import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-zoom.css';


// ─── Status Definitions ──────────────────────────────────────────────────────
const STATUS_LIST = [
    { key: 'all', label: 'Total', shortLabel: '#', badgeClass: 'badge-total', dotColor: '#64748b' },
    { key: 'available', label: 'Available', shortLabel: 'A', badgeClass: 'badge-available', dotColor: '#22c55e' },
    { key: 'booked', label: 'Booked', shortLabel: 'B', badgeClass: 'badge-booked', dotColor: '#f59e0b' },
    { key: 'holded', label: 'Hold', shortLabel: 'H', badgeClass: 'badge-hold', dotColor: '#0ea5e9' },
    { key: 'sold', label: 'Sold', shortLabel: 'S', badgeClass: 'badge-sold', dotColor: '#ef4444' },
];

const FILTER_TYPES = [
    { key: 'plotno', label: 'Plot No.' },
    { key: 'facing', label: 'Facing' },
    { key: 'corner', label: 'Corner' },
    { key: 'plotsize', label: 'Plot Size' },
];

const FACINGS = ['North', 'South', 'East', 'West'];
const CORNERS = ['North-East', 'North-West', 'South-East', 'South-West'];
const SIZES = [500, 1000, 1500, 2000, 2500, 3000];

// ─── Helpers ─────────────────────────────────────────────────────────────────
function comparePlotNumbers(a, b) {
    const valA = String(a.plotName ?? a.plotNo ?? '');
    const valB = String(b.plotName ?? b.plotNo ?? '');
    return valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' });
}

function getStatusCount(plotList, statusKey) {
    if (statusKey === 'all') return plotList.length;
    return plotList.filter(p => p.plotStatus === statusKey).length;
}

function filterPlots(plotList, activeStatus, filterMode, filterType, filterValues) {
    let result = activeStatus === 'all'
        ? plotList
        : plotList.filter(p => p.plotStatus === activeStatus);

    if (filterMode) {
        if (filterType === 'plotno' && filterValues.plotno) {
            const q = filterValues.plotno.toLowerCase();
            result = result.filter(p => String(p.plotName ?? p.plotNo ?? '').toLowerCase().includes(q));
        } else if (filterType === 'facing' && filterValues.facing) {
            result = result.filter(p => p.facing1 === filterValues.facing || (p.facing && p.facing.toLowerCase().includes(filterValues.facing.toLowerCase())));
        } else if (filterType === 'corner') {
            result = result.filter(p => p.cornerPlot === 'Yes');
            if (filterValues.corner) {
                const normVal = filterValues.corner.toLowerCase().replace(/[^a-z]/g, '');
                const parts = filterValues.corner.toLowerCase().split('-');
                result = result.filter(p => {
                    const fStr = ((p.facing || '') + ' ' + (p.facing1 || '')).toLowerCase().replace(/[^a-z]/g, '');
                    if (fStr.includes(normVal)) return true;
                    if (parts.length === 2) {
                        const rawF = ((p.facing || '') + ' ' + (p.facing1 || '')).toLowerCase();
                        if (rawF.includes(parts[0]) && rawF.includes(parts[1])) return true;
                    }
                    return false;
                });
            }
        } else if (filterType === 'plotsize') {
            const minV = filterValues.minSize ? parseInt(filterValues.minSize) : null;
            const maxV = filterValues.maxSize ? parseInt(filterValues.maxSize) : null;
            if (minV !== null) result = result.filter(p => parseInt(p.totalSqft) >= minV);
            if (maxV !== null) result = result.filter(p => parseInt(p.totalSqft) <= maxV);
        }
    }

    return [...result].sort(comparePlotNumbers);
}

// Mock useTranslations hook for fallback translations when i18n is not set up
const useTranslations = () => (key) => undefined;

// ─── Main Component ───────────────────────────────────────────────────────────
function PromoterApp(props) {
    const params = useParams();
    const id = props.id;
    const flatno = props.flatno;
    const locale = props.locale;
    const router = useRouter();
    const zoomRef = useRef(null);
    var API_URL = "/extapi/";

    // ── Data state ──
    const [plotList, setPlotList] = useState([]);
    const [title1, setTitle1] = useState("");
    const [imageSrc, setImageSrc1] = useState("");
    const [address, setAddress] = useState("");
    const [totalplots, setTotalplots] = useState("");
    const [available, setAvailable] = useState("");
    const [booked, setBooked] = useState("");
    const [sold, setSold] = useState("");
    const [holded, setHolded] = useState("");
    const [dtcp, setDtcp] = useState("");
    const [rera, setRera] = useState("");
    const [loan, setLoan] = useState("");
    const [shareLink, setShareLink] = useState("");
    const [emi, setEmi] = useState("");
    const [map, setMap] = useState("");
    const [videos, setVideos] = useState([]);
    const [imageDatas, setImageDatas] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [imageTop, setImageTop] = useState("");
    const [imageLeft, setImageLeft] = useState("");
    const [imageRight, setImageRight] = useState("");
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // ── UI state ──
    const [selectedPlot, setSelectedPlot] = useState('');
    const [markType, setMarkType] = useState(0);
    const [popupData, setPopupData] = useState(null);  // plot modal

    // Status-bar & bottom sheet
    const [activeStatus, setActiveStatus] = useState(null);   // null = no chip selected
    const [sheetOpen, setSheetOpen] = useState(false);

    // Filter mode
    const [filterMode, setFilterMode] = useState(false);
    const [filterType, setFilterType] = useState('plotno');
    const [filterValues, setFilterValues] = useState({
        plotno: '', facing: '', corner: '', minSize: '', maxSize: ''
    });

    // Show-sqft toggle on overview cards
    const [showSqft, setShowSqft] = useState(false);

    // Canvas class for map highlights
    const [canvasClass, setCanvasClass] = useState('all');

    // ── Helpers: normalize API responses ──
    // API returns { success, msg, data: [...] } wrapper
    // property_det field mapping: property_name→title, property_image→img, etc.
    // mark_type is a string ("Full"/"Box"/"Circle"), normalized to numeric (1/0/2)
    // polygon_points is wrapped: [{full:[...]}, {box:{...}}, {circle:{...}}]
    // plot_status uses "Available"→available, "Booked"→booked, "Holded"/"Hold"→holded, "Registered"/"Sold"→sold

    function normalizeMarkType(markType) {
        if (markType === "Full" || markType === "full") return 1;
        if (markType === "Box" || markType === "box") return 0;
        if (markType === "Circle" || markType === "circle") return 2;
        // Fallback for already-numeric values
        if (markType === 0 || markType === "0") return 0;
        if (markType === 1 || markType === "1") return 1;
        if (markType === 2 || markType === "2") return 2;
        return 0;
    }

    function normalizePolygonPoints(raw, markType) {
        // raw is an array like [{full:[{x,y},...]}] or [{box:{x,y,w,h}}] or [{circle:{x,y,r}}]
        if (!raw || !Array.isArray(raw) || raw.length === 0) return raw;
        const entry = raw[0];
        if (markType === 1 && entry.full) {
            return entry.full.map(p => ({ x: p.x * 0.5, y: p.y * 0.5 })); // array of {x, y} points
        }
        if (markType === 0 && entry.box) {
            return {
                x: entry.box.x * 0.5,
                y: entry.box.y * 0.5,
                w: entry.box.w * 0.5,
                h: entry.box.h * 0.5
            };
        }
        if (markType === 2 && entry.circle) {
            return {
                x: entry.circle.x * 0.5,
                y: entry.circle.y * 0.5,
                r: entry.circle.r * 0.5
            };
        }
        // Fallback: return raw
        return raw;
    }

    function normalizePlotStatus(status) {
        if (!status) return 'available';
        const lower = status.toLowerCase();
        if (lower === 'available') return 'available';
        if (lower === 'booked') return 'booked';
        if (lower === 'holded' || lower === 'hold') return 'holded';
        if (lower === 'registered' || lower === 'sold') return 'sold';
        return lower;
    }

    // ── Fetch data ──
    useEffect(() => {
        const img = new Image();
        const url = API_URL + "select/property_det";
        const startTime = Date.now();
        const minLoadingTime = 2000; // Keep minimum loader duration for smooth transition/aesthetics

        const fetchData = async () => {
            console.log("[DEBUG] fetchData called, id:", id, "url:", url);
            try {
                console.log("[DEBUG] Starting property_det fetch...");
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        sqlData: "*",
                        where: "id=\$1",
                        whereData: [id],
                    }),
                });
                if (!response.ok) throw new Error(`property_det HTTP error! status: ${response.status}`);
                const rawData = await response.json();
                // Unwrap API response: { success, msg, data: [...] }
                const propertyRow = rawData.data && rawData.data.length > 0 ? rawData.data[0] : {};

                // Fetch plot_det ordered by plot_name as unsigned integer
                const response1 = await fetch(API_URL + "select/plot_det", {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        sqlData: "*",
                        where: "property_id=\$1",
                        whereData: [id],
                        orderBy: "CAST(plot_name AS INTEGER)",
                    }),
                });
                if (!response1.ok) throw new Error(`plot_det HTTP error! status: ${response1.status}`);
                const rawData1 = await response1.json();
                // Unwrap API response
                const plotRows = rawData1.data || [];

                // Fetch booking_mas joined with file_mas to filter by property_id (non-fatal — no bookings is OK)
                let bookingMap = {}; // plotId (uuid) -> bookingType
                try {
                    const response2 = await fetch(API_URL + "select/booking_mas a inner join file_mas b on a.file_id=b.id", {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            sqlData: "a.selected_plots, a.booking_type",
                            where: "b.property_id=$1 AND a.is_active=true AND a.is_cancel=false",
                            whereData: [id],
                        }),
                    });
                    if (response2.ok) {
                        const rawData2 = await response2.json();
                        // Unwrap API response
                        const bookingRows = rawData2.data || [];
                        bookingRows.forEach(row => {
                            const plots = row.selected_plots || [];
                            const bType = row.booking_type || "";
                            plots.forEach(plotId => {
                                bookingMap[plotId] = bType;
                            });
                        });
                    } else {
                        console.warn("booking_mas returned status:", response2.status, "— treating as no bookings");
                    }
                } catch (bookingErr) {
                    console.warn("Failed to fetch booking_mas:", bookingErr.message, "— treating as no bookings");
                }

                // Build plot data array with booking info
                const plotData = plotRows
                    .filter(res2 => res2.polygon_points !== null && res2.polygon_points !== undefined)
                    .map(res2 => {
                        // Match by plot UUID against booking_mas.selected_plots
                        const bookingType = bookingMap[res2.id] || "";
                        const printBooking = bookingType && bookingType !== "In" ? bookingType : "";
                        const isHome = res2.is_home ? "isHome" : "";

                        // Normalize mark_type from string ("Full"/"Box"/"Circle") to numeric (1/0/2)
                        const numericMarkType = normalizeMarkType(res2.mark_type);

                        // Unwrap and normalize polygon_points based on mark type
                        let rawPolygon = typeof res2.polygon_points === 'string'
                            ? JSON.parse(res2.polygon_points)
                            : res2.polygon_points;
                        const polygonPoints = normalizePolygonPoints(rawPolygon, numericMarkType);

                        // Normalize plot_status: "Registered"→available, etc.
                        const normalizedStatus = normalizePlotStatus(res2.plot_status);

                        // For Box type, use imagex/y/w/h from polygon_points if zeros
                        let imagex = res2.imagex ? res2.imagex * 0.5 : 0;
                        let imagey = res2.imagey ? res2.imagey * 0.5 : 0;
                        let imagew = res2.imagew ? res2.imagew * 0.5 : 0;
                        let imageh = res2.imageh ? res2.imageh * 0.5 : 0;
                        if (numericMarkType === 0 && rawPolygon && rawPolygon[0] && rawPolygon[0].box) {
                            const box = rawPolygon[0].box;
                            if (!imagex && !imagey) {
                                imagex = box.x * 0.5;
                                imagey = box.y * 0.5;
                                imagew = box.w * 0.5;
                                imageh = box.h * 0.5;
                            }
                        }

                        return {
                            plotNo: res2.plot_no,
                            isHome: isHome,
                            bookingType: printBooking,
                            plotName: res2.plot_name,
                            markType: numericMarkType,
                            polygonPoints: polygonPoints,
                            imagex: imagex,
                            imagey: imagey,
                            imagew: imagew,
                            imageh: imageh,
                            totalSqft: res2.total_sqft,
                            road_length: res2.road_length,
                            plot_north: res2.plot_north,
                            plot_south: res2.plot_south,
                            plot_east: res2.plot_east,
                            plot_west: res2.plot_west,
                            facing: res2.facing,
                            facing1: res2.facing ? res2.facing.replace(/,/g, " ") : "",
                            cornerPlot: (
                                res2.corner_plot === true ||
                                res2.corner_plot === 1 ||
                                (typeof res2.corner_plot === 'string' && !['no', 'false', '0', ''].includes(res2.corner_plot.toLowerCase().trim())) ||
                                (res2.facing && /north-east|north-west|south-east|south-west|north,east|north,west|south,east|south,west|ne|nw|se|sw/i.test(res2.facing))
                            ) ? 'Yes' : 'No',
                            plotStatus: normalizedStatus,
                            remarks: res2.remarks || "",
                        };
                    })
                    .sort(comparePlotNumbers);
                setPlotList(plotData);
                console.log("[DEBUG] plotData count:", plotData.length, "plotRows:", plotRows.length);
                console.log("[DEBUG] propertyRow:", JSON.stringify(propertyRow).substring(0, 300));
                if (plotData.length > 0) {
                    console.log("[DEBUG] first plot:", JSON.stringify(plotData[0]).substring(0, 300));
                }

                // Map API field names to component state
                const propertyImage = propertyRow.property_image || "";
                const imgUrl = propertyImage ? API_URL + propertyImage : "";
                setTitle1(propertyRow.property_name || "");
                setImageSrc1(propertyImage);
                setAddress(propertyRow.address || "");
                setTotalplots(propertyRow.no_of_plots || "");
                setDtcp(propertyRow.dtcp_approved ? "Yes" : "No");
                setRera(propertyRow.rera_approved ? "Yes" : "No");
                setLoan(propertyRow.loan_option ? "Yes" : "No");
                setEmi(propertyRow.emi_option ? "Yes" : "No");
                setFacilities(propertyRow.facilities || []);
                setVideos(propertyRow.youtube_url ? [propertyRow.youtube_url] : []);
                setMap(propertyRow.site_location || "");
                setImageDatas(propertyRow.imageData || []);
                setImageTop(propertyRow.image_top || "");
                setImageLeft(propertyRow.image_left || "");
                setImageRight(propertyRow.image_right || "");
                setShareLink(propertyRow.share_link || "");

                // Compute counts from plot data (not from property row)
                const avail = plotData.filter(p => p.plotStatus === 'available').length;
                const book = plotData.filter(p => p.plotStatus === 'booked').length;
                const hold = plotData.filter(p => p.plotStatus === 'holded').length;
                const sl = plotData.filter(p => p.plotStatus === 'sold').length;
                setAvailable(avail);
                setBooked(book);
                setSold(sl);
                setHolded(hold);

                const finishLoading = () => {
                    const elapsed = Date.now() - startTime;
                    const remaining = Math.max(0, minLoadingTime - elapsed);
                    setTimeout(() => setLoading(false), remaining);
                };

                if (propertyImage) {
                    img.src = imgUrl;
                    img.onload = () => {
                        setDimensions({ width: img.width / 2, height: img.height / 2 });
                        finishLoading();
                    };
                    img.onerror = () => {
                        console.error("Error loading image:", imgUrl);
                        finishLoading();
                    };
                } else {
                    finishLoading();
                }

                if (flatno !== "0") {
                    setSelectedPlot("p" + flatno);
                    setTimeout(() => {
                        document.getElementById('resetZoom')?.click();
                        document.getElementById("viewFullScreen").checked = false;
                        document.getElementById("btnZoom")?.click();
                    }, 3000);
                }
            } catch (err) {
                console.error("Error fetching data:", err);
                setError(err.message);
                const elapsed = Date.now() - startTime;
                const remaining = Math.max(0, minLoadingTime - elapsed);
                setTimeout(() => setLoading(false), remaining);
            }
        };

        fetchData();
    }, [id]);

    // ── Derived filtered list for bottom sheet ──
    const filteredCards = filterPlots(
        plotList, activeStatus ?? 'all', filterMode, filterType, filterValues
    );

    // ── Status chip click ──
    const handleStatusChipClick = useCallback((statusKey) => {
        if (activeStatus === statusKey && sheetOpen) {
            setSheetOpen(false);
            return;
        }
        setActiveStatus(statusKey);
        setSheetOpen(true);
        // Also update map canvas class
        setCanvasClass(statusKey);
        const mainImg = document.getElementById('mainImg');
        if (mainImg) {
            mainImg.className = '';
            mainImg.classList.add(statusKey);
        }
        document.getElementById('resetZoom')?.click();
    }, [activeStatus, sheetOpen]);

    // ── Filter icon click ──
    const handleFilterToggle = useCallback(() => {
        if (filterMode) {
            // exit filter mode, reset everything
            setFilterMode(false);
            setFilterType('plotno');
            setFilterValues({ plotno: '', facing: '', corner: '', minSize: '', maxSize: '' });
            setSheetOpen(false);
            setActiveStatus(null);
            setCanvasClass('all');
            const mainImg = document.getElementById('mainImg');
            if (mainImg) { mainImg.className = ''; mainImg.classList.add('all'); }
            document.getElementById('resetZoom')?.click();
        } else {
            // enter filter mode
            setFilterMode(true);
            const status = activeStatus ?? 'all';
            setActiveStatus(status);
            setSheetOpen(true);
        }
    }, [filterMode, activeStatus]);

    // ── Filter value change (real-time) ──
    const handleFilterValueChange = useCallback((key, val) => {
        setFilterValues(prev => ({ ...prev, [key]: val }));
        setSheetOpen(true);
    }, []);

    // ── Filter type change ──
    const handleFilterTypeChange = useCallback((newType) => {
        setFilterType(newType);
        setFilterValues({ plotno: '', facing: '', corner: '', minSize: '', maxSize: '' });
        setSheetOpen(true);
    }, []);

    // Automatically open the bottom sheet when any filter setting or value changes
    useEffect(() => {
        if (filterMode) {
            setSheetOpen(true);
        }
    }, [filterType, filterValues, filterMode]);

    // ── Overview card click → zoom & modal ──
    const handleCardClick = useCallback((plot) => {
        setSheetOpen(false);
        setMarkType(Number(plot.markType));
        setSelectedPlot('p' + plot.plotNo);
        setTimeout(() => {
            document.getElementById('resetZoom')?.click();
            document.getElementById("viewFullScreen").checked = false;
            setTimeout(() => {
                document.getElementById("btnZoom")?.click();
            }, 150);
        }, 50);
        setTimeout(() => {
            setPopupData(plot);
        }, 400);
    }, []);

    // ── Canvas plot click → modal ──
    const handleRectangleClick = useCallback((rect) => {
        setPopupData(rect);
    }, []);

    // ── Modal close ──
    const closePopup = useCallback(() => setPopupData(null), []);

    // ── Back / Close page ──
    const handleBack = () => {
        if (typeof window !== 'undefined') {
            if (window.FlutterClose) {
                window.FlutterClose.postMessage('close');
            } else if (window.FlutterBack) {
                window.FlutterBack.postMessage('back');
            }
            try {
                window.close();
            } catch (e) {
                console.error("Window close error:", e);
            }
            setTimeout(() => {
                if (!window.closed) {
                    if (window.history && window.history.length > 1) {
                        router.back();
                    } else {
                        router.push('/');
                    }
                }
            }, 100);
        }
    };

    // ── Share ──
    const handleShare = () => {
        const url = shareLink || (typeof window !== 'undefined' ? window.location.href : '');
        const text = title1 ? `${title1} - ${url}` : url;
        if (typeof window !== 'undefined' && window.FlutterShare) {
            window.FlutterShare.postMessage(text);
        } else if (typeof navigator !== 'undefined' && navigator.share) {
            navigator.share({ title: title1, text: title1, url })
                .catch(console.error);
        } else if (typeof window !== 'undefined') {
            window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, '_blank');
        }
    };

    const handleSharefloat = (plotNo) => {
        const url = shareLink || (typeof window !== 'undefined' ? window.location.href : '');
        const text = title1 ? `${title1} (Plot ${plotNo}) - ${url}` : `Plot ${plotNo} - ${url}`;
        if (typeof window !== 'undefined' && window.FlutterShare) {
            window.FlutterShare.postMessage(text);
        } else if (typeof navigator !== 'undefined' && navigator.share) {
            navigator.share({ title: title1, text: text, url })
                .catch(console.error);
        } else if (typeof window !== 'undefined') {
            window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, '_blank');
        }
    };


    // ── Polygon centroid ──
    function getPolygonCentroid(points) {
        let area = 0, cx = 0, cy = 0;
        const n = points.length;
        for (let i = 0; i < n; i++) {
            const { x: x0, y: y0 } = points[i];
            const { x: x1, y: y1 } = points[(i + 1) % n];
            const a = x0 * y1 - x1 * y0;
            area += a; cx += (x0 + x1) * a; cy += (y0 + y1) * a;
        }
        area *= 0.5; cx /= (6 * area); cy /= (6 * area);
        return { x: cx, y: cy };
    }

    // ── Select plot (sidebar dropdown) ──
    const handleSelectPlotDropdown = (e) => {
        document.getElementById('resetZoom')?.click();
        document.getElementById("viewFullScreen").checked = false;
        setSelectedPlot(e.target.value);
        if (e.target.value === "0") return;
        setTimeout(() => document.getElementById("btnZoom")?.click(), 100);
    };

    // ── Counts ──
    const statusCounts = {
        all: plotList.length,
        available: plotList.filter(p => p.plotStatus === 'available').length,
        booked: plotList.filter(p => p.plotStatus === 'booked').length,
        holded: plotList.filter(p => p.plotStatus === 'holded').length,
        sold: plotList.filter(p => p.plotStatus === 'sold').length,
    };

    // ── Available sizes derived from plotList ──
    const allSizes = [...new Set(plotList.map(p => parseInt(p.totalSqft)).filter(Boolean))].sort((a, b) => a - b);
    const sizeOptions = allSizes.length > 0 ? allSizes : SIZES;

    if (loading) return (
        <div className='preloader'>
            <h3><span>Please wait</span><br />{title1} layout<br /><span>is loading...</span></h3>
        </div>
    );
    if (error) return <div>Error: {error}</div>;

    // ── Visible status chips in filter mode ──
    const visibleChips = filterMode
        ? STATUS_LIST.filter(s => s.key === (activeStatus ?? 'all'))
        : STATUS_LIST;

    return (
        <div className="layout-container-new">
            <input type="checkbox" id="viewFullCanvas" className="dis0" />
            <input type="checkbox" id="viewFullScreen" className="dis0" />

            {/* ── Top Header ── */}
            <header className="top-header-new">
                <div className="header-left">
                    <button
                        type="button"
                        className="back-btn-new"
                        onClick={handleBack}
                        title="Back"
                        aria-label="Back"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <div className="header-title-container">
                        <h2 className="header-title-new">{title1}</h2>
                        <span className="header-subtitle-new">
                            Total Plots: {plotList.length}
                        </span>
                    </div>
                </div>
                <div className="header-right">
                    <button className="share-btn-new" onClick={handleShare}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-whatsapp" viewBox="0 0 16 16">
                            <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
                        </svg>
                        <span>{'Share'}</span>
                    </button>
                    <button className="reset-btn-new" onClick={() => document.getElementById('resetZoom')?.click()}>
                        {'Reset'}
                    </button>
                </div>
            </header>

            {/* ── Workspace Body ── */}
            <div className="workspace-body-new">
                <div className="left-content-new">

                    {/* Direction labels */}
                    <span className="directions north"><b>{imageTop}</b></span>
                    <span className="directions west"><b>{imageLeft}</b></span>
                    <span className="directions east"><b>{imageRight}</b></span>

                    {/* Canvas zoom */}
                    <TransformWrapper initialScale={1} initialPositionX={200} initialPositionY={100} maxScale={3}>
                        {({ zoomToElement, resetTransform }) => (
                            <React.Fragment>
                                <button id="btnZoom" className="dis0" onClick={() => {
                                    const el = markType === 1 ? selectedPlot.replace('p', 'pd') : selectedPlot;
                                    zoomToElement(el);
                                    document.querySelectorAll('.blink').forEach(e => e.classList.remove('blink'));
                                    document.getElementById(selectedPlot)?.classList.add('blink');
                                }}>Zoom</button>
                                <button id="resetZoom" className="dis0" onClick={() => resetTransform()}>Reset</button>

                                <TransformComponent>
                                    <div style={{ position: 'relative', width: dimensions.width, height: dimensions.height }}>
                                        <img src={API_URL + imageSrc} width={dimensions.width} height={dimensions.height} alt="layout" id="mainImg" className={canvasClass} />
                                        {plotList.map((rect) => {
                                            if (rect.markType === 0) {
                                                return (
                                                    <div
                                                        key={rect.plotNo}
                                                        data-plot-size={rect.totalSqft}
                                                        className={`rectangle-box all-box ${rect.facing1} ${rect.isHome} ${rect.plotStatus} ${rect.cornerPlot === 'Yes' ? 'corner' : ''}`}
                                                        id={"p" + rect.plotNo}
                                                        style={{ position: "absolute", left: `${rect.imagex}px`, top: `${rect.imagey}px`, width: `${rect.imagew}px`, height: `${rect.imageh}px` }}
                                                        onClick={() => handleRectangleClick(rect)}
                                                    />
                                                );
                                            } else if (rect.markType === 1) {
                                                return (
                                                    <div key={rect.plotNo + 2000} className={`all-box ${rect.facing1} ${rect.isHome} ${rect.plotStatus} ${rect.cornerPlot === 'Yes' ? 'corner' : ''}`} data-plot-size={rect.totalSqft}>
                                                        <div
                                                            className={`polygon-box ${rect.facing1} ${rect.plotStatus} ${rect.isHome} ${rect.cornerPlot === 'Yes' ? 'corner' : ''}`}
                                                            onClick={() => handleRectangleClick(rect)}
                                                            style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'auto', overflow: 'visible', clipPath: `polygon(${rect.polygonPoints.map(p => `${p.x}px ${p.y}px`).join(', ')})`, width: '100%', height: '100%' }}
                                                            id={`p${rect.plotNo}`}
                                                        />
                                                        <div
                                                            className="rectangle-box"
                                                            id={"pd" + rect.plotNo}
                                                            key={rect.plotNo + 1000}
                                                            style={{ position: "absolute", left: `${getPolygonCentroid(rect.polygonPoints).x}px`, top: `${getPolygonCentroid(rect.polygonPoints).y}px`, width: `16px`, height: `16px` }}
                                                        />
                                                    </div>
                                                );
                                            } else {
                                                const circle = rect.polygonPoints;
                                                const diameter = circle.r * 2;
                                                return (
                                                    <div
                                                        key={rect.plotNo}
                                                        className={`circle-box all-box ${rect.facing1} ${rect.plotStatus} ${rect.isHome} ${rect.cornerPlot === 'Yes' ? 'corner' : ''}`}
                                                        data-plot-size={rect.totalSqft}
                                                        onClick={() => handleRectangleClick(rect)}
                                                        style={{ position: 'absolute', left: `${circle.x - (circle.r + 1)}px`, top: `${circle.y - (circle.r + 1)}px`, width: `${diameter}px`, height: `${diameter}px`, borderRadius: '50%' }}
                                                        id={`p${rect.plotNo}`}
                                                    />
                                                );
                                            }
                                        })}
                                    </div>
                                </TransformComponent>
                            </React.Fragment>
                        )}
                    </TransformWrapper>

                    {/* ── Canvas backdrop overlay ── */}
                    <div className={`bottom-sheet-overlay ${sheetOpen ? 'sheet-visible' : ''}`} onClick={() => setSheetOpen(false)} />

                    {/* ── Status Bar Wrapper: sheet grows upward, bar sits below ── */}
                    <div className="status-bar-wrapper">

                        {/* Bottom Sheet — anchored to top of status bar, same width */}
                        <div className={`bottom-sheet ${sheetOpen ? 'sheet-open' : ''}`}>
                            {/* Drag handle */}
                            <div className="sheet-handle-bar" onClick={() => setSheetOpen(false)}>
                                <div className="sheet-handle" />
                            </div>

                            {/* Mini header: status dropdown + count + sqft toggle */}
                            <div className="sheet-mini-header">
                                <div className="sheet-title-group">
                                    <div className={`status-dropdown-wrapper ${(STATUS_LIST.find(s => s.key === (activeStatus ?? 'all')) || STATUS_LIST[0]).badgeClass}`}>
                                        <span
                                            className="dot"
                                            style={{ backgroundColor: (STATUS_LIST.find(s => s.key === (activeStatus ?? 'all')) || STATUS_LIST[0]).dotColor }}
                                        />
                                        <span className="selected-text">
                                            {(STATUS_LIST.find(s => s.key === (activeStatus ?? 'all')) || STATUS_LIST[0]).label} ({statusCounts[activeStatus ?? 'all'] ?? 0})
                                        </span>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="dropdown-arrow">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                                        </svg>
                                        <select
                                            className="sheet-status-select-overlay"
                                            value={activeStatus ?? 'all'}
                                            onChange={(e) => handleStatusChipClick(e.target.value)}
                                        >
                                            {STATUS_LIST.map(s => (
                                                <option key={s.key} value={s.key}>
                                                    {s.label} ({statusCounts[s.key] ?? 0})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="sheet-header-actions">
                                    <span className="sheet-count">{filteredCards.length} plots</span>
                                    <button
                                        className={`sqft-toggle-btn ${showSqft ? 'sqft-active' : ''}`}
                                        onClick={() => setShowSqft(v => !v)}
                                    >
                                        {showSqft ? 'Hide Sqft' : 'Show Sqft'}
                                    </button>
                                </div>
                            </div>

                            {/* Plot Cards */}
                            <div className="sheet-cards-area">
                                {filteredCards.length === 0 ? (
                                    <div className="sheet-empty">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                                        </svg>
                                        <p>No plots match your filters</p>
                                    </div>
                                ) : (
                                    <div className="sheet-cards-grid">
                                        {filteredCards.map(plot => (
                                            <button
                                                key={plot.plotNo}
                                                className={`plot-card ${plot.plotStatus}`}
                                                onClick={() => handleCardClick(plot)}
                                            >
                                                <span className="card-plot-no">{plot.plotName}</span>
                                                {showSqft && <span className="card-sqft">{plot.totalSqft} sqft</span>}
                                                {plot.cornerPlot === 'Yes' && <span className="card-corner-dot" title="Corner Plot" />}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>{/* end bottom-sheet */}

                        {/* ── Floating Status Bar — filter controls inline in filter mode ── */}
                        <div className={`floating-status-bar ${filterMode ? 'filter-mode-active' : ''}`}>
                            {/* Status chips */}
                            {visibleChips.map(s => (
                                <button
                                    key={s.key}
                                    className={`status-chip ${s.badgeClass} ${activeStatus === s.key ? 'chip-active' : ''}`}
                                    onClick={() => handleStatusChipClick(s.key)}
                                >
                                    <span className="dot" style={{ backgroundColor: s.dotColor }} />
                                    <span className="chip-label chip-label-full">{s.label}</span>
                                    <span className="chip-label chip-label-short">{s.shortLabel}</span>
                                    <span className="chip-val">{statusCounts[s.key] ?? 0}</span>
                                </button>
                            ))}

                            {/* Inline filter controls — between active chip and × button */}
                            {filterMode && (
                                <div
                                    className="inline-filter-controls"
                                    onFocus={() => setSheetOpen(true)}
                                    onClick={() => setSheetOpen(true)}
                                    onMouseDown={() => setSheetOpen(true)}
                                    onTouchStart={() => setSheetOpen(true)}
                                    onKeyDown={() => setSheetOpen(true)}
                                    onInput={() => setSheetOpen(true)}
                                >
                                    <select
                                        className="filter-type-mini"
                                        value={filterType}
                                        onChange={e => handleFilterTypeChange(e.target.value)}
                                        onFocus={() => setSheetOpen(true)}
                                        onClick={() => setSheetOpen(true)}
                                        onMouseDown={() => setSheetOpen(true)}
                                        onTouchStart={() => setSheetOpen(true)}
                                    >
                                        {FILTER_TYPES.map(ft => (
                                            <option key={ft.key} value={ft.key}>{ft.label}</option>
                                        ))}
                                    </select>

                                    <div className="filter-control-mini">
                                        {filterType === 'plotno' && (
                                            <input
                                                className="filter-input-mini"
                                                type="text"
                                                placeholder="Plot no..."
                                                value={filterValues.plotno}
                                                onChange={e => handleFilterValueChange('plotno', e.target.value)}
                                                onFocus={() => setSheetOpen(true)}
                                                onClick={() => setSheetOpen(true)}
                                                onMouseDown={() => setSheetOpen(true)}
                                                onTouchStart={() => setSheetOpen(true)}
                                                autoFocus
                                            />
                                        )}
                                        {filterType === 'facing' && (
                                            <select
                                                className="filter-select-mini"
                                                value={filterValues.facing}
                                                onChange={e => handleFilterValueChange('facing', e.target.value)}
                                                onFocus={() => setSheetOpen(true)}
                                                onClick={() => setSheetOpen(true)}
                                                onMouseDown={() => setSheetOpen(true)}
                                                onTouchStart={() => setSheetOpen(true)}
                                            >
                                                <option value="">All</option>
                                                {FACINGS.map(f => <option key={f} value={f}>{f}</option>)}
                                            </select>
                                        )}
                                        {filterType === 'corner' && (
                                            <select
                                                className="filter-select-mini"
                                                value={filterValues.corner}
                                                onChange={e => handleFilterValueChange('corner', e.target.value)}
                                                onFocus={() => setSheetOpen(true)}
                                                onClick={() => setSheetOpen(true)}
                                                onMouseDown={() => setSheetOpen(true)}
                                                onTouchStart={() => setSheetOpen(true)}
                                            >
                                                <option value="">All</option>
                                                {CORNERS.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        )}
                                        {filterType === 'plotsize' && (
                                            <div className="size-pair-mini">
                                                <select
                                                    className="filter-select-mini-xs"
                                                    value={filterValues.minSize}
                                                    onChange={e => handleFilterValueChange('minSize', e.target.value)}
                                                    onFocus={() => setSheetOpen(true)}
                                                    onClick={() => setSheetOpen(true)}
                                                    onMouseDown={() => setSheetOpen(true)}
                                                    onTouchStart={() => setSheetOpen(true)}
                                                >
                                                    <option value="">Min</option>
                                                    {sizeOptions.map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                                <span className="size-dash">—</span>
                                                <select
                                                    className="filter-select-mini-xs"
                                                    value={filterValues.maxSize}
                                                    onChange={e => handleFilterValueChange('maxSize', e.target.value)}
                                                    onFocus={() => setSheetOpen(true)}
                                                    onClick={() => setSheetOpen(true)}
                                                    onMouseDown={() => setSheetOpen(true)}
                                                    onTouchStart={() => setSheetOpen(true)}
                                                >
                                                    <option value="">Max</option>
                                                    {sizeOptions.map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Filter toggle button (rightmost) */}
                            <button
                                className={`filter-toggle-btn ${filterMode ? 'filter-btn-active' : ''}`}
                                onClick={handleFilterToggle}
                                title={filterMode ? 'Exit filter mode' : 'Filter plots'}
                            >
                                {filterMode ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="filter-svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="filter-svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
                                    </svg>
                                )}
                            </button>
                        </div>{/* end floating-status-bar */}

                    </div>{/* end status-bar-wrapper */}

                </div>{/* end left-content-new */}
            </div>{/* end workspace-body-new */}


            {/* ── Plot Detail Modal ── */}
            {popupData && (
                <div className="plot-modal-overlay" onClick={closePopup}>
                    <div className="plot-modal-card" onClick={e => e.stopPropagation()}>
                        <div className={`plot-modal-header ${popupData.plotStatus}`}>
                            <div className="modal-header-left">
                                <div>
                                    <div className="modal-plot-label">Plot</div>
                                    <div className="modal-plot-no">{popupData.plotName}</div>
                                </div>
                                <div className="modal-header-divider"></div>
                                <div className="modal-header-status-badge">
                                    {popupData.plotStatus}
                                </div>
                            </div>
                            <button className="modal-close-btn" onClick={closePopup}>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="plot-modal-body">
                            <div className="modal-status-badge">
                                <span className={`status-pill ${popupData.plotStatus}`}>{popupData.plotStatus}</span>
                            </div>
                            <div className="modal-details-grid">
                                {[
                                    { label: 'Total Sqft', val: popupData.totalSqft },
                                    { label: 'Facing', val: popupData.facing },
                                    { label: 'Corner Plot', val: popupData.cornerPlot },
                                    { label: 'Road Length (Sqft)', val: popupData.road_length },
                                    { label: 'North (Sqft)', val: popupData.plot_north },
                                    { label: 'South (Sqft)', val: popupData.plot_south },
                                    { label: 'East (Sqft)', val: popupData.plot_east },
                                    { label: 'West (Sqft)', val: popupData.plot_west },
                                ].map(row => (
                                    <div key={row.label} className="modal-detail-row">
                                        <span className="detail-label">{row.label}</span>
                                        <span className="detail-val">{row.val}</span>
                                    </div>
                                ))}
                            </div>
                            <button className="modal-share-btn" onClick={() => handleSharefloat(popupData.plotNo)}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-whatsapp" viewBox="0 0 16 16">
                                    <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
                                </svg>
                                Share on WhatsApp
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PromoterApp;
